/**
 * Zentrale Format-Helpers (deutsche Anzeige-Konventionen).
 *
 * Regel aus den Coding-Standards: lokale Kalenderdaten sind "YYYY-MM-DD"-Strings —
 * niemals via toISOString() konvertieren (Timezone-Shift). fmtDate parst solche
 * Strings deshalb ohne Umweg ueber Date.
 */

/** Formatiert ein Datum als DD.MM.YYYY. Akzeptiert "YYYY-MM-DD", ISO-Strings oder Date. */
export function fmtDate(value: string | Date | null | undefined): string {
  if (!value) return ""
  if (typeof value === "string") {
    // Lokales Kalenderdatum "YYYY-MM-DD" (ggf. mit Zeitanteil) direkt zerlegen
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (m) return `${m[3]}.${m[2]}.${m[1]}`
    value = new Date(value)
  }
  if (Number.isNaN(value.getTime())) return ""
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(value)
}

/**
 * Formatiert Urlaubstage (Decimal-String der API oder Zahl) kompakt mit Komma:
 * "5.00" -> "5", "2.5" -> "2,5". Ungueltige Werte -> "0".
 */
export function fmtDays(value: string | number | null | undefined): string {
  const n = typeof value === "string" ? Number(value) : (value ?? 0)
  if (Number.isNaN(n)) return "0"
  return (Math.round(n * 100) / 100).toString().replace(".", ",")
}

/** Formatiert Stunden als Dezimalzahl mit Komma, z. B. 7.5 -> "7,50" */
export function fmtHours(hours: number): string {
  return hours.toFixed(2).replace(".", ",")
}

/** Formatiert einen Betrag als Euro, z. B. 1234.5 -> "1.234,50 €" */
export function fmtEuro(amount: number): string {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(amount)
}

/** Faelligkeits-Ampel (PRD/Skill: rot < 30 Tage, orange < 90 Tage, sonst gruen) */
export type Urgency = "red" | "orange" | "green"

export function urgencyColor(days: number): Urgency {
  if (days < 30) return "red"
  if (days < 90) return "orange"
  return "green"
}

/** Tailwind-Klassen passend zur Ampelfarbe (fuer Badges) */
export function urgencyClasses(days: number): string {
  switch (urgencyColor(days)) {
    case "red":
      return "bg-destructive/10 text-destructive border-destructive/30"
    case "orange":
      return "bg-warning/10 text-amber-700 border-warning/40"
    case "green":
      return "bg-success/10 text-success border-success/30"
  }
}
